import {useState} from 'react';
import {Pressable, ScrollView, View} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {Text} from '../../Components';
import {PageContainer} from '../../Containers';
import {useTheme} from '../../Hooks';
import getStyles from './Training.styles';

type TrainingModule = {
  id: string;
  title: string;
  subtitle: string;
  overview: string;
  learningPoints: string[];
  practicePoints: string[];
};

const TRAINING_MODULES: TrainingModule[] = [
  {
    id: 'mood',
    title: 'Module 1',
    subtitle: 'Depression & mood disorders',
    overview:
      'Recognise common mood symptoms, understand their effect on daily functioning, and identify appropriate first steps.',
    learningPoints: [
      'Identify persistent low mood, loss of interest, sleep changes, and reduced functioning.',
      'Distinguish short-term distress from concerns that need further assessment.',
      'Plan supportive care, follow-up, or referral using local clinical pathways.',
    ],
    practicePoints: [
      'Ask clear, non-judgemental questions about mood and daily life.',
      "Document the young person's concerns and the impact on home, school, and relationships.",
      'Follow local safety and escalation procedures when risk concerns arise.',
    ],
  },
  {
    id: 'anxiety',
    title: 'Module 2',
    subtitle: 'Anxiety & trauma-related concerns',
    overview:
      'Build confidence recognising anxiety and trauma-related presentations while using a calm, trauma-informed approach.',
    learningPoints: [
      'Recognise generalised anxiety, social anxiety, avoidance, and post-traumatic stress symptoms.',
      'Explore triggers, duration, physical symptoms, and disruption to everyday activities.',
      'Identify when brief support is appropriate and when specialist review is needed.',
    ],
    practicePoints: [
      'Give the young person time and choice when discussing difficult experiences.',
      'Avoid pressing for unnecessary trauma details during an initial screening.',
      'Agree on a practical next step and explain the follow-up plan clearly.',
    ],
  },
  {
    id: 'psychosis',
    title: 'Module 3',
    subtitle: 'Psychosis & bipolar presentations',
    overview:
      'Recognise possible psychosis or bipolar presentations and respond with respectful communication and timely escalation.',
    learningPoints: [
      'Recognise changes in perception, beliefs, behaviour, sleep, energy, and functioning.',
      'Consider physical health, substance use, medication, and other possible contributors.',
      'Know when the presentation requires urgent or specialist assessment.',
    ],
    practicePoints: [
      'Stay calm, listen carefully, and avoid arguing about unusual experiences.',
      'Include family or trusted support people when appropriate and consented to.',
      'Use local referral and emergency pathways for severe or rapidly changing symptoms.',
    ],
  },
];

const Training: React.FC = () => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [expandedModuleId, setExpandedModuleId] = useState<string | null>(null);

  return (
    <PageContainer withSafeArea withPadding>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.titleBand}>
          <View style={styles.titleIconContainer}>
            <MaterialCommunityIcons name="school-outline" size={24} style={styles.titleIcon} />
          </View>
          <View style={styles.titleText}>
            <Text
              fontSize={25}
              lineHeight={31}
              weight="black"
              accessibilityRole="header"
              style={styles.title}
            >
              Training Modules
            </Text>
            <Text style={styles.titleSubtitle}>Clinical learning and practice guidance</Text>
          </View>
        </View>

        <View style={styles.guidanceBadge}>
          <MaterialCommunityIcons name="check-decagram" size={15} style={styles.guidanceIcon} />
          <Text weight="semiBold" style={styles.guidanceBadgeText}>
            Informed by WHO mhGAP guidance
          </Text>
        </View>

        <View style={styles.introPanel}>
          <Text style={styles.introText}>
            Interactive modules for clinicians and primary care workers supporting young people with
            mental health and psychosocial concerns. Select a module to review its learning and
            practice points.
          </Text>
        </View>

        <Text weight="bold" style={styles.sectionLabel}>
          Core modules
        </Text>

        {TRAINING_MODULES.map((module) => {
          const isExpanded = expandedModuleId === module.id;

          return (
            <View key={module.id}>
              <Pressable
                accessibilityRole="button"
                accessibilityState={{expanded: isExpanded}}
                accessibilityLabel={`${module.title}: ${module.subtitle}`}
                onPress={() => setExpandedModuleId(isExpanded ? null : module.id)}
                style={({pressed}) => [
                  styles.moduleButton,
                  isExpanded ? styles.moduleButtonExpanded : null,
                  pressed ? styles.moduleButtonPressed : null,
                ]}
              >
                <View style={styles.moduleText}>
                  <View style={styles.moduleNumber}>
                    <Text weight="bold" style={styles.moduleNumberText}>
                      {module.title.replace('Module ', '')}
                    </Text>
                  </View>
                  <View style={styles.moduleLabel}>
                    <Text weight="bold" fontSize={16} style={styles.moduleTitle}>
                      {module.title}
                    </Text>
                    <Text fontSize={13} style={styles.moduleSubtitle}>
                      {module.subtitle}
                    </Text>
                  </View>
                </View>
                <MaterialCommunityIcons
                  name={isExpanded ? 'chevron-up' : 'chevron-right'}
                  size={25}
                  style={styles.chevron}
                />
              </Pressable>

              {isExpanded ? (
                <View style={styles.moduleDetail}>
                  <Text style={styles.detailOverview}>{module.overview}</Text>

                  <Text weight="bold" style={styles.detailHeading}>
                    Learning objectives
                  </Text>
                  {module.learningPoints.map((point) => (
                    <Text key={point} style={styles.detailItem}>
                      {`- ${point}`}
                    </Text>
                  ))}

                  <Text weight="bold" style={styles.detailHeading}>
                    In clinical practice
                  </Text>
                  {module.practicePoints.map((point) => (
                    <Text key={point} style={styles.detailItem}>
                      {`- ${point}`}
                    </Text>
                  ))}
                </View>
              ) : null}
            </View>
          );
        })}

        <View
          accessibilityRole="button"
          accessibilityState={{disabled: true}}
          style={styles.futureModule}
        >
          <View style={styles.moduleText}>
            <View style={styles.futureModuleNumber}>
              <MaterialCommunityIcons
                name="dots-horizontal"
                size={18}
                style={styles.futureModuleNumberIcon}
              />
            </View>
            <View style={styles.moduleLabel}>
              <Text weight="bold" fontSize={16} style={styles.futureModuleTitle}>
                Further Modules
              </Text>
              <Text fontSize={13} style={styles.futureModuleSubtitle}>
                Substance use, epilepsy & more
              </Text>
            </View>
          </View>
          <MaterialCommunityIcons name="lock-outline" size={21} style={styles.futureModuleIcon} />
        </View>
      </ScrollView>
    </PageContainer>
  );
};

export default Training;
